import type { OptionProduct } from '@linktivity/ars-neo';

// `unitRatio` is a per-option-product weight: when optionRule.unitRestriction is
// evaluated, one selected unit of that product counts as `unitRatio` units (2 →
// counts as two, 0.5 → counts as a half). A missing or zero ratio keeps the
// product out of the restriction entirely: it contributes nothing and gets no
// ceiling of its own.
const OPTION_QUANTITY_SIGNIFICANT_DIGITS = 12;

const normalizeDecimal = (value: number): number =>
  Number(value.toPrecision(OPTION_QUANTITY_SIGNIFICANT_DIGITS));

export const normalizeOptionQuantity = (quantity?: number | null): number => {
  if (typeof quantity !== 'number' || !Number.isFinite(quantity)) {
    return 0;
  }
  return normalizeDecimal(Math.max(0, quantity));
};

/** Only products with a positive ratio take part in the restriction. */
export const isOptionUnitRatioActive = (unitRatio?: number): boolean =>
  normalizeOptionQuantity(unitRatio) > 0;

export interface WeightedOptionQuantity {
  quantity?: number | null;
  unitRatio?: number;
}

export const getWeightedOptionQuantity = ({
  quantity,
  unitRatio
}: WeightedOptionQuantity): number =>
  normalizeOptionQuantity(
    normalizeOptionQuantity(quantity) * normalizeOptionQuantity(unitRatio)
  );

export const sumWeightedOptionQuantities = (
  items: ReadonlyArray<WeightedOptionQuantity>
): number =>
  normalizeOptionQuantity(
    items.reduce((total, item) => total + getWeightedOptionQuantity(item), 0)
  );

export interface CanAddNextOptionUnitOptions {
  availableWeightedQuantity: number;
  quantity: number;
  unitRatio?: number;
}

export const canAddNextOptionUnit = ({
  availableWeightedQuantity,
  quantity,
  unitRatio
}: CanAddNextOptionUnitOptions): boolean => {
  const ratio = normalizeOptionQuantity(unitRatio);
  if (ratio === 0) return true;

  const nextWeightedQuantity = getWeightedOptionQuantity({
    quantity: normalizeOptionQuantity(quantity) + 1,
    unitRatio: ratio
  });

  return (
    nextWeightedQuantity <= normalizeOptionQuantity(availableWeightedQuantity)
  );
};

export interface SelectedOptionQuantity {
  productContentId: string;
  displayInfo?: { totalQuantity?: number };
}

/**
 * Weighted total across every selected option product, each one weighted by the
 * ratio of its own product content.
 */
export const sumSelectedOptionWeightedQuantities = (
  optionProducts: ReadonlyArray<OptionProduct>,
  selectedOptions: ReadonlyArray<SelectedOptionQuantity>
): number => {
  const ratioByContentId = new Map<string, number | undefined>();
  for (const optionProduct of optionProducts) {
    const productContentId = optionProduct.productContent?.id;
    if (productContentId) {
      ratioByContentId.set(productContentId, optionProduct.unitRatio);
    }
  }

  return sumWeightedOptionQuantities(
    selectedOptions.map(option => ({
      quantity: option.displayInfo?.totalQuantity,
      unitRatio: ratioByContentId.get(option.productContentId)
    }))
  );
};

export interface GetOptionRawQuantityLimitOptions {
  remainingWeightedQuantity: number;
  unitRatio?: number;
}

/**
 * How many raw units of one option product fit into the weighted allowance that
 * is left. Rounds down so the weighted total never passes the maximum, which
 * means a ratio that doesn't divide the allowance leaves a gap the user can't
 * fill — the ceiling notice is what explains that dead end.
 * `undefined` means "no ceiling": a ratio of 0 weighs nothing, so no number of
 * units can ever reach the maximum.
 */
export const getOptionRawQuantityLimit = ({
  remainingWeightedQuantity,
  unitRatio
}: GetOptionRawQuantityLimitOptions): number | undefined => {
  const ratio = normalizeOptionQuantity(unitRatio);
  if (ratio === 0) return undefined;

  return Math.floor(
    normalizeDecimal(normalizeOptionQuantity(remainingWeightedQuantity) / ratio)
  );
};
